import { useCallback } from 'react';
import { BACKEND_URL } from '../../config';

const api = async (path, options = {}) => {
  const res = await fetch(`${BACKEND_URL}/api/service-os${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...options.headers },
    ...options,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || 'Request failed');
  }
  return res.json();
};

export function useServiceOS() {
  const getProfile = useCallback(() => api('/profile'), []);
  const patchProfile = useCallback((data) => api('/profile', { method: 'PATCH', body: JSON.stringify(data) }), []);
  const getCompletion = useCallback(() => api('/profile/completion'), []);
  const startOnboarding = useCallback(() => api('/onboarding/start', { method: 'POST' }), []);
  const completeOnboarding = useCallback((data) => api('/onboarding/complete', { method: 'POST', body: JSON.stringify(data) }), []);

  const getDashboard = useCallback(() => api('/dashboard'), []);

  const getLeads = useCallback((params = {}) => {
    const q = new URLSearchParams();
    if (params.status) q.set('status', params.status);
    if (params.limit) q.set('limit', params.limit);
    if (params.skip) q.set('skip', params.skip);
    return api(`/leads?${q.toString()}`);
  }, []);

  const getLead = useCallback((id) => api(`/leads/${id}`), []);
  const createLead = useCallback((data) => api('/leads', { method: 'POST', body: JSON.stringify(data) }), []);
  const updateLead = useCallback((id, data) => api(`/leads/${id}`, { method: 'PATCH', body: JSON.stringify(data) }), []);
  const markBooked = useCallback((id) => api(`/leads/${id}/mark-booked`, { method: 'POST' }), []);
  const markLost = useCallback((id, reason) => api(`/leads/${id}/mark-lost`, { method: 'POST', body: JSON.stringify({ reason }) }), []);
  const getAvailability = useCallback((id) => api(`/leads/${id}/availability`), []);
  const getFollowups = useCallback((id) => api(`/leads/${id}/followups`), []);

  const emailIntake = useCallback((data) => api('/intake/email', { method: 'POST', body: JSON.stringify(data) }), []);

  const generateReply = useCallback((leadId) => api(`/leads/${leadId}/generate-reply`, { method: 'POST' }), []);
  const updateReply = useCallback((id, data) => api(`/replies/${id}`, { method: 'PATCH', body: JSON.stringify(data) }), []);
  const approveReply = useCallback((id) => api(`/replies/${id}/approve`, { method: 'POST' }), []);
  const sendReply = useCallback((id) => api(`/replies/${id}/send`, { method: 'POST' }), []);

  const getJobs = useCallback((params = {}) => {
    const q = new URLSearchParams();
    if (params.status) q.set('status', params.status);
    if (params.date) q.set('date', params.date);
    if (params.limit) q.set('limit', params.limit);
    return api(`/jobs?${q.toString()}`);
  }, []);
  const createJob = useCallback((data) => api('/jobs', { method: 'POST', body: JSON.stringify(data) }), []);
  const updateJob = useCallback((id, data) => api(`/jobs/${id}`, { method: 'PATCH', body: JSON.stringify(data) }), []);
  const completeJob = useCallback((id) => api(`/jobs/${id}/complete`, { method: 'POST' }), []);
  const cancelJob = useCallback((id) => api(`/jobs/${id}/cancel`, { method: 'POST' }), []);

  const sendTestEmail = useCallback(() => api('/inbox/send-test', { method: 'POST' }), []);

  const getDeployment = useCallback(() => api('/deploy'), []);
  const createDeployment = useCallback((data) => api('/deploy', { method: 'POST', body: JSON.stringify(data) }), []);

  return {
    getProfile, patchProfile, getCompletion, startOnboarding, completeOnboarding,
    getDashboard,
    getLeads, getLead, createLead, updateLead, markBooked, markLost, getAvailability, getFollowups,
    emailIntake, sendTestEmail,
    generateReply, updateReply, approveReply, sendReply,
    getJobs, createJob, updateJob, completeJob, cancelJob,
    getDeployment, createDeployment,
  };
}
